import Button from "core/buttons/button/Button";
import InfoContext from "pages/InfoContext";
import SubcontractorCorporateInfoForm from "components/form/subcontractor/SubcontractorCorporateInfoForm";
import SubcontractorPersonalInfoForm from "components/form/subcontractor/SubcontractorPersonalInfoForm";
import _ from "lodash";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import {
  FILTERS_FOR_NAMES,
  POPUP_STATUS,
  QUERY_STATUS,
} from "constants/constants";
import { SUBCONTRACTOR_FORM_DEFAULT_VALUES } from "constants/forms";
import { formatReceivedData } from "utils/subcontractorHelper";
import { useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useUserContext } from "api/user/UserProvider";
import colors from "assets/styles/mixins/colors.scss";

const PersonalSubcontractor = ({
  setReadOnly,
  onSubmit,
  readOnly,
  startDate,
  getStatus,
  getCode,
}) => {
  const {
    clearErrors,
    control,
    formState: { errors },
    register,
    setValue,
    unregister,
    handleSubmit,
    watch,
    setError,
  } = useForm({
    defaultValues: SUBCONTRACTOR_FORM_DEFAULT_VALUES,
  });

  const onReset = () => {
    initForm();
    setReadOnly(true);
    clearErrors();
    setApiErrorResponse(null);
    window.scrollTo(0, 0);
  };

  const {
    branches,
    lang,
    userBySlug,
    usersByIds,
    searchUsers,
    MissionsWorkers,
    setIsValidated,
    editSubcontractorStatus,
    isValidationModalOpen,
    apiErrorResponse,
  } = useContext(InfoContext);
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const { setApiErrorResponse } = useUserContext();

  const apiErrorFieldsRef = useRef([]);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);
  useEffect(() => {
    if (apiErrorResponse && Object.keys(apiErrorResponse).length > 0) {
      Object.entries(apiErrorResponse).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });
      apiErrorFieldsRef.current = Object.keys(apiErrorResponse);
      setReadOnly(false);
    } else if (apiErrorFieldsRef.current.length > 0) {
      apiErrorFieldsRef.current.forEach((field) => clearErrors(field));
      apiErrorFieldsRef.current = [];
    }
  }, [apiErrorResponse, setError]);

  const initForm = () => {
    const defaultKeys = Object.keys(SUBCONTRACTOR_FORM_DEFAULT_VALUES);
    const formattedReceivedData = formatReceivedData({
      data: userBySlug,
      branches,
      lang,
      usersByIds,
      MissionsWorkers,
      namesByFilters,
    });
    Object.keys(formattedReceivedData).forEach((elm) => {
      if (defaultKeys.includes(elm) && formattedReceivedData[elm]) {
        setValue(elm, formattedReceivedData[elm]);
      }
    });
  };

  useEffect(() => {
    const id = [userBySlug?.manager_id]?.filter(Boolean);
    if (id?.length > 0) {
      getNamesByFilters({
        types: [FILTERS_FOR_NAMES.PERSON],
        id,
      });
    }
  }, [userBySlug]);

  useEffect(() => {
    startDate && setValue("contract_start_date", startDate);
  }, [startDate]);

  useEffect(() => {
    if (
      !_.isEmpty(userBySlug) &&
      namesByFilters?.length &&
      !isValidationModalOpen &&
      editSubcontractorStatus !== QUERY_STATUS.ERROR &&
      editSubcontractorStatus !== QUERY_STATUS.LOADING
    ) {
      initForm();
    }
  }, [userBySlug, namesByFilters, isValidationModalOpen, editSubcontractorStatus]);

  useEffect(() => {
    if (userBySlug) {
      const managerId = userBySlug?.manager_id;
      managerId && searchUsers({ ids: [userBySlug?.manager_id] });
    }
  }, [userBySlug]);

  const onError = (errors) => {
    if (!_.isEmpty(errors)) {
      getStatus(POPUP_STATUS.ERROR);
      getCode(FIELD_REQUIRED);
    }
  };

  const onGuardedSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await onSubmit(info);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit(onGuardedSubmit, onError)}
        id="create-subcontractor-form"
      >
        <SubcontractorPersonalInfoForm
          watch={watch}
          setError={setError}
          clearErrors={clearErrors}
          setValue={setValue}
          control={control}
          errors={errors}
          register={register}
          readOnly={readOnly}
          unregister={unregister}
        />
        <SubcontractorCorporateInfoForm
          control={control}
          register={register}
          unregister={unregister}
          setValue={(field, value) => {
            setValue(field, value);
            clearErrors(field);
          }}
          watch={watch}
          errors={errors}
          readOnly={readOnly}
          setErrors={setError}
        />
        {!readOnly && (
          <div style={{ marginTop: "50px" }} className="form-buttons">
            <Button
              className="btn-standard btn-outline"
              type="button"
              onClick={onReset}
              id="button-form-reset"
            >
              <FormattedMessage id="cancel" defaultMessage="Annuler" />
            </Button>
            <Button
              backgroundColor={
                editSubcontractorStatus === QUERY_STATUS.LOADING &&
                colors.disabledGrey
              }
              color="btn-medium gray-700"
              type="submit"
              id="button-form-validation"
              onClick={() => setIsValidated(true)}
              disabled={editSubcontractorStatus === QUERY_STATUS.LOADING}
            >
              <FormattedMessage
                id="button_form_validation_edition"
                defaultMessage="Valider l'édition"
              />
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default PersonalSubcontractor;
